import { useEffect, useId, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Extra text searched but not displayed, e.g. register synonyms (E-05). */
  keywords?: string[];
}

/**
 * A searchable single-select (docs/08 §8.3).
 *
 * "Typing filters the list, arrow keys navigate, Enter selects" - so
 * "Klebsiella pneumoniae" is `k` then Enter for most Gram-negative entries.
 *
 * Implements the ARIA combobox pattern so it is usable with a screen reader
 * (Y-02, Y-05), which is why this is hand-built rather than a styled <select>:
 * a native select cannot be filtered by typing more than one character.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder = "Type to search…",
  disabled,
  invalid,
  allowClear = true,
}: {
  id: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  allowClear?: boolean;
}) {
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => {
      if (option.label.toLowerCase().includes(needle)) return true;
      return option.keywords?.some((k) => k.toLowerCase().includes(needle)) ?? false;
    });
  }, [options, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Close when focus or a click leaves the widget.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const commit = (option: ComboboxOption | undefined) => {
    if (!option) return;
    onChange(option.value);
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setOpen(true);
        setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter":
        if (open && filtered.length > 0) {
          // Only swallow Enter when it is selecting something; otherwise let
          // it through so the form's own Enter handling still works (E-14).
          event.preventDefault();
          commit(filtered[activeIndex]);
        }
        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          setOpen(false);
          setQuery("");
        }
        break;
      case "Backspace":
        if (!query && selected && allowClear) onChange(null);
        break;
      default:
        break;
    }
  };

  const displayValue = open ? query : (selected?.label ?? "");

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered.length ? `${listboxId}-${activeIndex}` : undefined}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        disabled={disabled}
        className={cn(
          "h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-slate-900",
          "placeholder:text-slate-400 focus:border-brand-600 focus:outline-none",
          "focus:ring-2 focus:ring-brand-600 disabled:bg-slate-100",
          invalid && "border-blocking ring-1 ring-blocking",
        )}
        placeholder={selected ? selected.label : placeholder}
        value={displayValue}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {selected && allowClear && !open && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
        >
          Clear<span className="sr-only"> {selected.label}</span>
        </button>
      )}

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-sm text-slate-500">No matches</li>
          )}
          {filtered.map((option, index) => (
            <li
              key={option.value}
              id={`${listboxId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              className={cn(
                "cursor-pointer px-3 py-2 text-sm",
                index === activeIndex ? "bg-brand-50 text-brand-900" : "text-slate-700",
              )}
              onPointerDown={(event) => {
                event.preventDefault();
                commit(option);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
